import { faStop } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ChildUser, JoonUIColor, Typography } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import EndScreenTimeUseModal from "./EndScreenTimeUseModal"
import Button from "../../components/buttons/Button2"
import { QUERY_KEYS } from "../../constants"
import { useFamilyQuery } from "../../networking/queries"
import { useGetActiveScreenTimeSessionsQuery } from "../../networking/rewards/mutations"
import { FamilyQuerySelect } from "../../types"
import { ScreenTimeLog } from "../../types/rewards"
import { queryClient } from "../../util/queryClient"
import { getChildImage } from "../../util/util"

const ActiveScreenTimeSection = () => {
  const { data: activeScreenTimeSessions } =
    useGetActiveScreenTimeSessionsQuery()
  const { t } = useTranslation()

  if (!activeScreenTimeSessions || activeScreenTimeSessions?.length === 0)
    return null

  return (
    <div className="p-4 border border-accent rounded-md flex flex-col gap-4 bg-white">
      <Typography variant="bodyBold">{t("REWARD_ST_ACTIVE")}</Typography>
      {activeScreenTimeSessions?.map((session) => (
        <ActiveScreenTimeSession key={session?.id} session={session} />
      ))}
    </div>
  )
}

const ActiveScreenTimeSession = ({ session }: { session: ScreenTimeLog }) => {
  const { t } = useTranslation()
  const totalSeconds = Math.abs(session?.minutes * 60)
  const [remainingSeconds, setRemainingSeconds] = useState(
    totalSeconds - dayjs().diff(dayjs(session.date_created), "seconds")
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const childProfile = children?.find(
    (child) => child.user.id === session.user.id
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => prev - 1)
      if (remainingSeconds <= 0) {
        setRemainingSeconds(0)
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SCREEN_TIME_ACTIVE_SESSION],
        })
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [remainingSeconds])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const handleEndEarly = () => {
    setIsModalOpen(true)
  }

  return (
    <>
      <div className="p-3 border border-accent rounded-md bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={getChildImage(childProfile?.user as ChildUser | null)}
              alt={session.user.name}
              className="w-10 h-10 rounded-full object-contain"
            />
            <div className="flex flex-col">
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
              >
                {session.user.name}
              </Typography>

              <div className="flex flex-row items-center gap-2">
                <Typography variant="h3" color={JoonUIColor.text.primaryAccent}>
                  {formatTime(Math.max(0, remainingSeconds))}
                </Typography>
                <Typography variant="body" color={JoonUIColor.text.secondary}>
                  / {formatTime(totalSeconds)}
                </Typography>
              </div>
            </div>
          </div>
          <Button
            text={
              <div className="flex flex-row items-center gap-2">
                <FontAwesomeIcon icon={faStop} />
                <Typography variant="bodySmall">
                  {t("REWARD_END_EARLY")}
                </Typography>
              </div>
            }
            buttonType="secondary"
            size="small"
            onClick={handleEndEarly}
          />
        </div>
      </div>
      <EndScreenTimeUseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        session={session}
      />
    </>
  )
}

export default ActiveScreenTimeSection
